import { v4 as uuidv4 } from 'uuid';
import React, { useState, useEffect, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';
import axios from 'axios';
import { DataGrid,GridFooterContainer, GridPagination } from '@mui/x-data-grid';
import { apiUrl } from '../../apiUrl/ApiUrl';

export default function ToShipList() {
  const [newData, setNewData] = useState(null);
  const [model, setModel] = useState('出貨明細');
  const [loading, setLoading] = useState(false);
  const [totalRealGive, setTotalRealGive] = useState(0);

  // ★ 計算實交貨數總和
  useEffect(() => {
    const sum = (newData ?? []).reduce((acc, x) => acc + Number(x?.realGive ?? 0), 0);
    setTotalRealGive(sum);
  }, [newData]);

  const workMargin = { marginTop: '3%', marginBottom: '5%' };

  const handleDownloadExcel = async () => {
    if (!newData || newData.length === 0) {
      alert('沒有資料可以匯出！');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}/ToShip/ExportToExcel`, newData, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '出貨資料.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('匯出失敗：', error);
      alert('匯出失敗，請稍後再試！');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="container-fluid">
        <div className="row" style={workMargin}>
          <div className="col-5" />
          <div className="col-2">
            <h2>
              <span className="badge bg-success">{model}</span>
            </h2>
          </div>
          <div className="col-5">
            <h2>
              <span className="badge bg-dark">出貨總數: {totalRealGive}</span>
            </h2>
          </div>
        </div>

        <div className="row" style={workMargin}>
          <div className="col-6">
            <SelectFilter setNewData={setNewData} setLoading={setLoading} />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <h2>
              <span className="badge bg-dark">出貨資料</span>
              <button className="btn btn-success ms-3" onClick={handleDownloadExcel}>
                匯出 Excel
              </button>
            </h2>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {model === '出貨明細' ? (
              <ListGrid data={newData} setLoading={setLoading} />
            ) : (
              <div>目前還沒取得資料或取不到資料</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function CustomFooter({ totalRealGive, totalAmount }) {
  return (
    <GridFooterContainer>
      {/* SUM 列 */}
      <div
        style={{
          width: '80%',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 24,
          padding: '8px 16px',
          borderTop: '1px solid #ddd',
          background: '#fafafa',
          fontWeight: 700,
          boxSizing: 'border-box',
        }}
      >
        <div>合計</div>

        <div style={{ minWidth: 120, textAlign: 'right' }}>
          實交貨數：{Number(totalRealGive).toLocaleString()}
        </div>

        <div style={{ minWidth: 160, textAlign: 'right' }}>
          應收金額：
          {Number(totalAmount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      </div>

      {/* 原本的換頁按鈕 */}
      <GridPagination />
    </GridFooterContainer>
  );
}



/** ★ DataGrid 版本 */
function ListGrid({ data, setLoading }) {
  // ★ 產生 DataGrid 需要的 rows（保證有 id）
  const rows = useMemo(() => {
    return (data ?? []).map((r, idx) => ({
      id:
        r.id ??
        (r.toShipNum && r.serialNum && r.itemNum
          ? `${r.toShipNum}-${r.serialNum}-${r.itemNum}`
          : uuidv4()),
      ...r,
    }));
  }, [data]);

  const sums = useMemo(() => {
    const list = data ?? [];
    const totalRealGive = list.reduce((acc, r) => acc + Number(r?.realGive ?? 0), 0);
    const totalAmount = list.reduce((acc, r) => acc + Number(r?.amountReceivable ?? 0), 0);
    return { totalRealGive, totalAmount };
  }, [data]);


  // ★ 欄位定義
  const columns = useMemo(
    () => [
      {
        field: '_idx',
        headerName: '#',
        width: 60,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (params) =>
          params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      { field: 'client', headerName: '客戶', minWidth: 90, flex: 1, align: 'center', headerAlign: 'center' },
      { field: 'toShipDate', headerName: '出貨日期', minWidth: 115, flex: 0.8, align: 'center', headerAlign: 'center' },
      { field: 'toShipNum', headerName: '出貨單號', minWidth: 115, flex: 0.9, align: 'center', headerAlign: 'center' },
      { field: 'piNum', headerName: '批號', minWidth: 115, flex: 0.7, align: 'center', headerAlign: 'center' },
      { field: 'drawNum', headerName: '圖號', minWidth: 120, flex: 1, align: 'center', headerAlign: 'center' },

      { field: 'itemName', headerName: '物品名', minWidth: 150, flex: 1.4, align: 'center', headerAlign: 'center' },
      {
        field: 'realGive',
        headerName: '實交貨數',
        minWidth: 100,
        flex: 0.7,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (p) => (p.value == null ? '' : Number(p.value)),
      },
      { field: 'unit', headerName: '單位', minWidth: 80, flex: 0.5, align: 'center', headerAlign: 'center' },
      {
        field: 'orderPrice',
        headerName: '訂購單價',
        minWidth: 120,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (p) =>
          p.value == null
            ? ''
            : Number(p.value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        field: 'amountReceivable',
        headerName: '應收金額',
        minWidth: 140,
        flex: 0.9,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (p) =>
          p.value == null
            ? '0.00'
            : Number(p.value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      { field: 'orderNum', headerName: '訂單號碼', minWidth: 140, flex: 1, align: 'center', headerAlign: 'center' },
      { field: 'serialNum', headerName: '流水號', minWidth: 120, flex: 0.9, align: 'center', headerAlign: 'center' },
    ],
    []
  );

  return (
    <div style={{ height: 560, width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        disableRowSelectionOnClick
        rowHeight={38}
        headerHeight={32}
        localeText={{ noRowsLabel: '尚未查詢或查無資料' }}
        initialState={{
          pagination: { paginationModel: { pageSize: 50, page: 0 } },
        }}
        sx={{
          '& .MuiTablePagination-selectLabel': { display: 'none' },
          '& .MuiTablePagination-select': { display: 'none' },
          '& .MuiTablePagination-displayedRows': { margin: 0 }, // 顯示 "1–100 of xxx" 還會保留
        }}
        pageSizeOptions={[100]}
        slots={{
          footer: () => (
            <CustomFooter
              totalRealGive={sums.totalRealGive}
              totalAmount={sums.totalAmount}
            />
          ),
        }}
      />
    </div>
  );
}

function SelectFilter({ setNewData, setLoading }) {
  const getTaipeiDate = () => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const taipeiTime = new Date(utc + 8 * 60 * 60000);
    const year = taipeiTime.getFullYear();
    const month = String(taipeiTime.getMonth() + 1).padStart(2, '0');
    const day = String(taipeiTime.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const [start, setStart] = useState(getTaipeiDate());
  const [end, setEnd] = useState(getTaipeiDate());
  const [client, setClient] = useState('');
  const [piNum, setPiNum] = useState('');
  const [drawNum, setDrawNum] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const RealapiUrl = `${apiUrl}/ToShip/GetMainsBetween`;
      const params = { start, client, end, piNum, drawNum };
      const response = await axios.get(RealapiUrl, { params });
      // ★ 這裡後端回傳的陣列 key 為 toShips
      setNewData(response.data?.toShips ?? []);
    } catch (e) {
      console.error(e);
      alert('查詢失敗');
      setNewData([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-4">
            <label htmlFor="inputText1">
              起始日
              <input
                type="text"
                placeholder="yyyy/mm/dd"
                className="form-control"
                id="inputText1"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </label>
          </div>
          <div className="col-4">
            <label htmlFor="inputText2">
              截止日
              <input
                type="text"
                placeholder="yyyy/mm/dd"
                className="form-control"
                id="inputText2"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </label>
          </div>
          <div className="col-4">
            <label htmlFor="inputText3">
              客戶
              <input
                type="text"
                className="form-control"
                id="inputText3"
                value={client}
                onChange={(e) => setClient(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-4">
            <label htmlFor="inputText4">
              批號
              <input
                type="text"
                className="form-control"
                id="inputText4"
                value={piNum}
                onChange={(e) => setPiNum(e.target.value)}
              />
            </label>
          </div>
          <div className="col-4">
            <label htmlFor="inputText5">
              圖號
              <input
                type="text"
                className="form-control"
                id="inputText5"
                value={drawNum}
                onChange={(e) => setDrawNum(e.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="row" style={{ textAlign: 'center' }}>
          <div className="col-12">
            <button type="submit" className="btn btn-dark">
              提交
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
